import * as THREE from 'three';
import { MeshBVH, acceleratedRaycast, disposeBoundsTree } from 'three-mesh-bvh';
import { CadObject } from '../../../models/cad-object.model';
import { PullDirection } from '../../../models/pull-direction.model';

// Patch Mesh.prototype.raycast for BVH acceleration
THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

export interface DraftAnalysisResult {
  draftArea: number;
  visibleArea: number;
  hiddenArea: number;
}

const VISIBLE_COLOR = new THREE.Color('aqua');
const HIDDEN_COLOR = new THREE.Color('red');
const DRAFT_COLOR = new THREE.Color('yellow');
const BASE_COLOR = new THREE.Color('LightGray');

const NEAR_VERTICAL_TOLERANCE = 4.5; // degrees
const EXTRUDE_PERCENTAGE = 0.15;

/**
 * Applies draft analysis coloring to all CAD object meshes.
 * Colors faces based on their visibility from the pull direction:
 * - Aqua: visible faces (accessible from pull direction)
 * - Red: hidden faces (occluded or facing away)
 * - Yellow: draft faces (visible but with insufficient draft angle)
 *
 * Ported from mbox mould-flow-tool-vector-selector.component.ts
 */
export function applyDraftAnalysis(
  cadObjects: CadObject[],
  pullDirection: PullDirection,
  maxDraftAngle: number,
): DraftAnalysisResult {
  let totalDraftArea = 0;
  let totalVisibleArea = 0;
  let totalHiddenArea = 0;

  const pullVec = new THREE.Vector3(
    pullDirection.x,
    pullDirection.y,
    pullDirection.z,
  );
  const pullLength = pullVec.length();
  if (pullLength === 0) {
    return { draftArea: 0, visibleArea: 0, hiddenArea: 0 };
  }
  const normalizedPull = pullVec.clone().divideScalar(pullLength);

  const analysisMeshes = cadObjects
    .map((cadObject) => cadObject.mesh)
    .filter((mesh): mesh is THREE.Mesh => mesh instanceof THREE.Mesh);

  const raycaster = new THREE.Raycaster();
  for (const cadObject of cadObjects) {
    if (!cadObject.mesh) continue;

    const mesh = cadObject.mesh;
    const geometry = mesh.geometry as THREE.BufferGeometry;
    const positionAttribute = geometry.getAttribute(
      'position',
    ) as THREE.BufferAttribute;
    if (!positionAttribute) continue;

    const vertexCount = positionAttribute.count;
    if (vertexCount === 0) continue;

    // Ensure we have a color buffer attribute
    let colorAttribute = geometry.getAttribute(
      'color',
    ) as THREE.BufferAttribute | null;
    const originalColorAttribute = geometry.getAttribute(
      'originalColor',
    ) as THREE.BufferAttribute | null;
    if (!colorAttribute) {
      const colors = new Float32Array(vertexCount * 3);
      // Initialize with base color
      for (let i = 0; i < vertexCount; i++) {
        colors[i * 3] = BASE_COLOR.r;
        colors[i * 3 + 1] = BASE_COLOR.g;
        colors[i * 3 + 2] = BASE_COLOR.b;
      }
      colorAttribute = new THREE.Float32BufferAttribute(colors, 3);
      geometry.setAttribute('color', colorAttribute);
    }

    // Switch material to use vertex colors
    const material = mesh.material as THREE.MeshStandardMaterial;
    material.vertexColors = true;
    material.color.set(0xffffff); // White base so vertex colors show correctly
    material.needsUpdate = true;

    // Build BVH for accelerated raycasting
    // Dispose existing BVH if present to avoid memory leaks
    geometry.disposeBoundsTree();
    geometry.boundsTree = new MeshBVH(geometry);

    const indexAttribute = geometry.getIndex();
    const faceCount = indexAttribute
      ? indexAttribute.count / 3
      : vertexCount / 3;

    for (let fi = 0; fi < faceCount; fi++) {
      const a = indexAttribute ? indexAttribute.getX(fi * 3) : fi * 3;
      const b = indexAttribute ? indexAttribute.getX(fi * 3 + 1) : fi * 3 + 1;
      const c = indexAttribute ? indexAttribute.getX(fi * 3 + 2) : fi * 3 + 2;

      let color = BASE_COLOR.clone();
      if (originalColorAttribute) {
        color.fromBufferAttribute(originalColorAttribute, a);
      }

      // Get vertex positions in world space
      const posA = new THREE.Vector3()
        .fromBufferAttribute(positionAttribute, a)
        .applyMatrix4(mesh.matrixWorld);
      const posB = new THREE.Vector3()
        .fromBufferAttribute(positionAttribute, b)
        .applyMatrix4(mesh.matrixWorld);
      const posC = new THREE.Vector3()
        .fromBufferAttribute(positionAttribute, c)
        .applyMatrix4(mesh.matrixWorld);

      const triangle = new THREE.Triangle(posA, posB, posC);
      const faceNormal = triangle.getNormal(new THREE.Vector3());
      const faceArea = triangle.getArea();

      // Calculate draft angle (angle between face normal and pull direction)
      const draftAngle = faceNormal.angleTo(normalizedPull) * (180 / Math.PI);

      // Determine raycast origin — near-vertical faces get extruded
      const raycastOrigin = triangle.getMidpoint(new THREE.Vector3());
      if (
        draftAngle > 90 - NEAR_VERTICAL_TOLERANCE &&
        draftAngle < 90 + NEAR_VERTICAL_TOLERANCE
      ) {
        raycastOrigin.addScaledVector(
          faceNormal,
          EXTRUDE_PERCENTAGE * Math.sqrt(faceArea),
        );
      }

      // Raycast along pull direction to detect occlusion
      raycaster.set(raycastOrigin, normalizedPull);
      const intersects = raycaster.intersectObjects(analysisMeshes, false);
      const didIntersect = intersects.some(
        (intersect) =>
          intersect.distance > 1e-6 &&
          (intersect.object !== mesh || intersect.faceIndex !== fi),
      );

      if (draftAngle > 90 + NEAR_VERTICAL_TOLERANCE || didIntersect) {
        // Hidden face
        totalHiddenArea += faceArea;
        color = HIDDEN_COLOR;
      } else {
        // Visible face
        totalVisibleArea += faceArea;
        color = VISIBLE_COLOR;
        if (
          draftAngle > 90 - maxDraftAngle &&
          draftAngle < 90 + maxDraftAngle
        ) {
          // Visible but insufficient draft
          totalDraftArea += faceArea;
          color = DRAFT_COLOR;
        }
      }

      // Apply color to all 3 vertices of this face
      colorAttribute.setXYZ(a, color.r, color.g, color.b);
      colorAttribute.setXYZ(b, color.r, color.g, color.b);
      colorAttribute.setXYZ(c, color.r, color.g, color.b);
    }

    colorAttribute.needsUpdate = true;
  }

  return {
    draftArea: totalDraftArea,
    visibleArea: totalVisibleArea,
    hiddenArea: totalHiddenArea,
  };
}

/**
 * Clears draft analysis coloring and restores base material.
 */
export function clearDraftAnalysis(cadObjects: CadObject[]): void {
  for (const cadObject of cadObjects) {
    if (!cadObject.mesh) continue;

    const mesh = cadObject.mesh;
    const geometry = mesh.geometry as THREE.BufferGeometry;
    const material = mesh.material as THREE.MeshStandardMaterial;
    const colorAttribute = geometry.getAttribute('color') as
      THREE.BufferAttribute | undefined;
    const originalColorAttribute = geometry.getAttribute('originalColor') as
      THREE.BufferAttribute | undefined;

    if (colorAttribute && originalColorAttribute) {
      for (let i = 0; i < colorAttribute.count; i++) {
        const color = new THREE.Color().fromBufferAttribute(
          originalColorAttribute,
          i,
        );
        colorAttribute.setXYZ(i, color.r, color.g, color.b);
      }
      colorAttribute.needsUpdate = true;
    }

    // Remove vertex colors and restore base material
    material.vertexColors = true;
    material.color.set(0xffffff);
    material.needsUpdate = true;

    // Clean up BVH
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (geometry as any).boundsTree = null;
  }
}
